import {useCallback} from 'react';

import {useAppDispatch} from 'src/store/hooks';

import {Status, usePushNotification} from '@pb/kit-component-push-notification';

import type {ApiDispatchForToolkit} from 'src/openapi/admin/types';


export type NotificationMessages = {
    success: string,
    error: string,
    exception: string,
};

export type NotificationConfig = {
    successTimeout?: number,
    errorTimeout?: number,
    exceptionTimeout?: number,
};

type NotificationOptions<T> = {
    getErrorMessage?: (data?: T) => string | null | undefined,
};

type ExecuteWithNotificationsConfig<T> = NotificationConfig & NotificationOptions<T>;

export type ApiResult<T = unknown> = {
    success: boolean,
    data?: T,
};

type ApiAction = Parameters<ApiDispatchForToolkit>[0];

type ApiResponse<T> = {
    type: string,
    payload: {
        success?: boolean,
        error?: boolean,
        data?: T,
    },
};

const DEFAULT_SUCCESS_TIMEOUT = 3000;
const DEFAULT_ERROR_TIMEOUT = 5000;
const DEFAULT_EXCEPTION_TIMEOUT = 5000;

export const useApiWithNotifications = () => {
    const apiDispatch = useAppDispatch();
    const {onCreateNote} = usePushNotification();

    const executeWithNotifications = useCallback(
        async <T = unknown>(
            apiAction: unknown,
            messages: NotificationMessages,
            actionName: string,
            config?: ExecuteWithNotificationsConfig<T>,
        ): Promise<ApiResult<T>> => {
            const successTimeout = config?.successTimeout ?? DEFAULT_SUCCESS_TIMEOUT;
            const errorTimeout = config?.errorTimeout ?? DEFAULT_ERROR_TIMEOUT;
            const exceptionTimeout = config?.exceptionTimeout ?? DEFAULT_EXCEPTION_TIMEOUT;

            try {
                const result = await apiDispatch(apiAction as ApiAction) as ApiResponse<T>;

                if (result.payload.success) {
                    onCreateNote({
                        id: `${actionName}-success-${Date.now()}`,
                        message: messages.success,
                        status: Status.SUCCESS,
                        autoHideTimeout: successTimeout,
                    });
                    return {
                        success: true,
                        data: result.payload.data,
                    };
                }

                if (result.payload.error) {
                    const customErrorMessage = config?.getErrorMessage?.(result.payload.data);
                    onCreateNote({
                        id: `${actionName}-error-${Date.now()}`,
                        message: customErrorMessage || messages.error,
                        status: Status.ERROR,
                        autoHideTimeout: errorTimeout,
                    });
                }
                return {
                    success: false,
                    data: result.payload.data,
                };
            } catch {
                onCreateNote({
                    id: `${actionName}-exception-${Date.now()}`,
                    message: messages.exception,
                    status: Status.ERROR,
                    autoHideTimeout: exceptionTimeout,
                });
                return {
                    success: false,
                };
            }
        },
        [apiDispatch, onCreateNote],
    );

    return {executeWithNotifications};
};
